import { useState, useId, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { PlusCircle, Loader2, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { dataApi } from "@/lib/api/admin-api";
import type { Organization } from "@/types/organization";

const organizationFormSchema = z.object({
  name: z.string().min(1, t`Organization name is required`),
  slug: z
    .string()
    .min(1, t`Slug is required`)
    .regex(/^[a-z0-9-]+$/, t`Slug may only contain lowercase letters, numbers, and hyphens`),
  isActive: z.boolean().optional(),
});

type OrganizationFormValues = z.infer<typeof organizationFormSchema>;

function FormSection({
  title,
  description,
  children,
  noBorder,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  noBorder?: boolean;
}) {
  return (
    <div className={`flex ${noBorder ? "" : "border-b border-(--color-border)"}`}>
      <div className="w-[300px] shrink-0 bg-(--color-surface-muted) border-r border-(--color-border) p-5 flex flex-col gap-2">
        <h3 className="text-sm font-medium text-(--color-text-primary)">{title}</h3>
        {description && (
          <p className="text-sm text-(--color-text-secondary) leading-snug">{description}</p>
        )}
      </div>
      <div className="flex-1 p-5 flex flex-col gap-4">{children}</div>
    </div>
  );
}

function FormField({
  label,
  id,
  error,
  children,
}: {
  label: string;
  id?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 flex-1">
      <Label htmlFor={id} className="text-sm font-medium text-(--color-text-primary)">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-(--color-danger-text)">{error}</p>}
    </div>
  );
}

const inputClass = "";
const selectTriggerClass = "";

interface OrganizationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organization?: Organization;
}

export function OrganizationFormDialog({
  open,
  onOpenChange,
  organization,
}: OrganizationFormDialogProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const idPrefix = useId();
  const isEditMode = !!organization;

  const [submitError, setSubmitError] = useState<string | null>(null);
  const slugManuallyEdited = useRef(isEditMode);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationFormSchema),
    mode: "onBlur",
    defaultValues: {
      name: "",
      slug: "",
      isActive: true,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: organization?.name ?? "",
        slug: organization?.slug ?? "",
        isActive: organization?.isActive ?? true,
      });
      slugManuallyEdited.current = isEditMode;
    }
  }, [open, organization?.id, organization?.name, organization?.slug, organization?.isActive, reset, isEditMode]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setValue("name", name, { shouldValidate: true });
    if (!slugManuallyEdited.current) {
      const slug = name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/-{2,}/g, "-")
        .replace(/^-|-$/g, "");
      setValue("slug", slug, { shouldValidate: true });
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    slugManuallyEdited.current = true;
    setValue("slug", e.target.value, { shouldValidate: true });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset();
      slugManuallyEdited.current = isEditMode;
      setSubmitError(null);
    }
    onOpenChange(nextOpen);
  };

  const createMutation = useMutation({
    mutationFn: (data: OrganizationFormValues) => {
      return dataApi.createOrganization({
        name: data.name.trim(),
        slug: data.slug,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      handleOpenChange(false);
      toast.success(t`Organization created successfully`);
    },
    onError: (error: Error) => {
      setSubmitError(error.message || t`Failed to create organization`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: OrganizationFormValues) =>
      dataApi.updateOrganization(organization!.id, {
        name: data.name.trim(),
        slug: data.slug,
        isActive: data.isActive,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      handleOpenChange(false);
      toast.success(t`Organization updated successfully`);
    },
    onError: (error: Error) => {
      setSubmitError(error.message || t`Failed to update organization`);
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (data: OrganizationFormValues) => {
    setSubmitError(null);
    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[900px] p-0 overflow-hidden gap-0 border-none rounded-(--radius-lg)"
      >
        <div className="flex flex-col bg-(--color-surface-content) max-h-[90vh]">
          <div className="flex items-start justify-between p-5 border-b border-(--color-border)">
            <div className="flex flex-col gap-1.5">
              <DialogTitle className="text-2xl font-medium text-(--color-text-primary) leading-none">
                {isEditMode ? t`Edit Organization` : t`Create Organization`}
              </DialogTitle>
              <p className="text-base text-(--color-text-secondary) font-normal leading-none">
                {isEditMode
                  ? t`Update the organization information below.`
                  : t`Create a new organization to group concepts, users, and content.`}
              </p>
            </div>
            <DialogClose
              aria-label={t`Close dialog`}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
            >
              <X className="h-6 w-6" />
            </DialogClose>
          </div>

          <form
            id={`${idPrefix}-form`}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col flex-1 overflow-y-auto"
          >
            <FormSection
              title={t`Organization details`}
              description={t`Basic information about the organization.`}
              noBorder
            >
              <div className="flex gap-3">
                <FormField
                  label={t`Organization name`}
                  id={`${idPrefix}-name`}
                  error={errors.name?.message}
                >
                  <Input
                    id={`${idPrefix}-name`}
                    placeholder={t`Enter organization name...`}
                    {...register("name")}
                    onChange={handleNameChange}
                    className={`${inputClass} ${errors.name ? "border-(--color-danger)" : ""}`}
                    autoFocus
                  />
                </FormField>
                <FormField
                  label={t`Slug`}
                  id={`${idPrefix}-slug`}
                  error={errors.slug?.message}
                >
                  <Input
                    id={`${idPrefix}-slug`}
                    placeholder={t`organization-slug`}
                    {...register("slug")}
                    onChange={handleSlugChange}
                    className={`${inputClass} ${errors.slug ? "border-(--color-danger)" : ""}`}
                  />
                </FormField>
              </div>

              {isEditMode && (
                <FormField label={t`Status`}>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value === false ? "inactive" : "active"}
                        onValueChange={(v) => field.onChange(v === "active")}
                      >
                        <SelectTrigger className={selectTriggerClass}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">{t`Active`}</SelectItem>
                          <SelectItem value="inactive">{t`Inactive`}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>
              )}
            </FormSection>
          </form>

          <div className="p-5 border-t border-(--color-border) flex flex-col gap-3 bg-(--color-surface-content) shrink-0">
            {submitError && (
              <div className="flex items-center gap-2 rounded-lg bg-(--color-danger)/10 border border-(--color-danger)/20 px-4 py-2.5 text-sm text-(--color-danger-text)">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <p className="text-sm text-(--color-text-secondary)">
                {isEditMode
                  ? t`Changes will be applied immediately`
                  : t`Status defaults to Active • Organization ID auto-generated`}
              </p>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  className="w-[200px] h-[52px] rounded-(--radius-food-thumb) border-(--color-border) text-base font-medium text-(--color-text-primary)"
                >
                  {t`Cancel`}
                </Button>
                <Button
                  form={`${idPrefix}-form`}
                  type="submit"
                  disabled={isPending}
                  className="w-[200px] h-[52px] rounded-(--radius-food-thumb) bg-(--color-action) text-(--color-action-fg) text-base font-medium hover:bg-(--color-action)/90 disabled:opacity-50"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEditMode ? t`Save Changes` : t`Create Organization`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CreateOrganizationDialog() {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button className="gap-2" onClick={() => setOpen(true)}>
        <PlusCircle className="h-4 w-4" />
        {t`Create New Organization`}
      </Button>
      <OrganizationFormDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
